import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`/api/blog.json`)
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen">
      <Helmet>
        <title>Construction Blog Toronto | Commercial Building Tips & Insights | Oracle Project Group</title>
        <meta name="description" content="Expert construction insights, commercial building tips, and industry trends from Toronto's Oracle Project Group. Learn about interior fit-outs, renovations, design-build best practices." />
        <link rel="canonical" href="https://oracleprojectgroup.com/blog" />
        <meta property="og:title" content="Construction Blog Toronto | Oracle Project Group Insights" />
        <meta property="og:description" content="Expert construction tips, commercial building insights, and renovation advice for Toronto & GTA." />
        <meta property="og:url" content="https://oracleprojectgroup.com/blog" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Construction Blog Toronto | Oracle Project Group Insights" />
        <meta name="twitter:description" content="Expert construction tips, commercial building insights, and renovation advice for Toronto & GTA." />
        <meta name="keywords" content="construction blog Toronto, commercial building tips, renovation advice, construction industry insights, GTA construction news" />
      </Helmet>
      {/* Hero Section */}
      <header className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1581783458534-001a466b5487?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBpbnRlcmlvcnxlbnwwfHx8fDE3NjE2MDIzNTd8MA&ixlib=rb-4.1.0&q=85"
          alt="Blog - News and insights from Oracle Project Group"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Commercial Construction Blog Toronto - News & Insights</h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Stay updated with the latest construction trends, interior fit-out projects, and industry insights from Oracle Project Group in Toronto & GTA.
          </p>
        </div>
      </header>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
              <p className="mt-4 text-gray-600">Loading posts...</p>
            </div>
          ) : (
            <div className="space-y-12">
              {posts.map((post, index) => (
                <div
                  key={post.id}
                  className={`grid md:grid-cols-2 gap-8 items-center ${
                    index % 2 === 0 ? '' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={index % 2 === 0 ? 'order-1' : 'order-2'}>
                    <div className="relative h-80 rounded-lg overflow-hidden shadow-lg">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className={index % 2 === 0 ? 'order-2' : 'order-1'}>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-2">
                        <Calendar size={16} className="text-[#C6A45B]" />
                        <span>{new Date(post.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock size={16} className="text-[#C6A45B]" />
                        <span>{post.readTime} min read</span>
                      </div>
                    </div>
                    <h2 className="text-3xl font-bold text-[#0F1115] mb-4">{post.title}</h2>
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">{post.excerpt}</p>
                    <Link
                      to={`/blog/${post.id}`}
                      className="inline-flex items-center text-[#D4AF37] font-semibold hover:text-[#C19A2B] transition-colors"
                    >
                      Read More
                      <ArrowRight className="ml-2" size={20} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Blog;
